import { shallowEqual } from 'react-redux';
import { Alert, Banner, CloseButton, Text } from '@zillow/constellation-10';

import { PropertiesSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import listingClaimUtils from '../../../utils/listingClaimUtils';
import numberUtils from '../../../utils/numberUtils';

import PropertiesPageConstants from '../PropertiesPageConstants';

import { BannerBox } from '../styles';

type Props = {
  displayPaymentsNotification: boolean;
  displayApplicationsNotification: boolean;
  displayLeasesNotification: boolean;
  displayListingClaimCompleteBanner: boolean;
  handleBannerCloseClick: (arg: (typeof PropertiesPageConstants)[keyof typeof PropertiesPageConstants]) => void;
};

const Notifications = ({
  displayPaymentsNotification,
  displayApplicationsNotification,
  displayLeasesNotification,
  displayListingClaimCompleteBanner,
  handleBannerCloseClick,
}: Props): JSX.Element => {
  const { isEnterpriseCustomer } = useSelector(
    (state) => ({
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
    }),
    shallowEqual,
  );

  const propertyConversion = useSelector((state) => PropertiesSelectors.makePropertyConversion(state));

  const displayListingConversionBanner =
    propertyConversion.count.processing > 0 || propertyConversion.count.pending > 0;

  const getConversionPercentage = (count: { [key: string]: number }) => {
    const conversionRate = listingClaimUtils.getConversionRate(count);
    return numberUtils.asPercent(conversionRate);
  };

  return (
    <div>
      {displayListingConversionBanner && (
        <BannerBox data-testid="PropertiesPage-display-container" isEnterpriseCustomer={isEnterpriseCustomer}>
          <Alert>
            {
              <Text textStyle="body">
                <b>{`${getConversionPercentage(propertyConversion.count)} complete`}</b>
                &nbsp;
                {/* eslint-disable-next-line max-len */}
                Conversion of your listings is in progress! You can access your listings once this is complete.
              </Text>
            }
          </Alert>
        </BannerBox>
      )}

      {displayListingClaimCompleteBanner && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => handleBannerCloseClick(PropertiesPageConstants.DISPLAY_LISTING_CLAIM_COMPLETE_BANNER)}
          >
            Select a property below to view and manage its information.
          </Banner>
        </BannerBox>
      )}

      {displayApplicationsNotification && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => handleBannerCloseClick(PropertiesPageConstants.DISPLAY_APPLICATIONS_NOTIFICATION)}
          >
            Applications are enabled for your properties. You can change application preferences anytime on the
            property&apos;s&nbsp;
            <em>Applications</em>
            &nbsp;tab.
          </Banner>
        </BannerBox>
      )}

      {displayPaymentsNotification && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => handleBannerCloseClick(PropertiesPageConstants.DISPLAY_PAYMENTS_NOTIFICATION)}
          >
            To manage your rent payments, click on&nbsp;
            <em>Payments</em>
            &nbsp;for any property below.
          </Banner>
        </BannerBox>
      )}

      {displayLeasesNotification && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => handleBannerCloseClick(PropertiesPageConstants.DISPLAY_LEASES_NOTIFICATION)}
          >
            For your properties with a&nbsp;
            <em>Leases</em>
            &nbsp;tab, click on it to create a custom lease.
          </Banner>
        </BannerBox>
      )}
    </div>
  );
};

export default Notifications;
